import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import ReactFinalForm from 'components/form/ReactFinalForm';

import { IOption } from 'interfaces';

import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonGroup,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useEffect, useState } from 'react';
import { MODELS_MAPS } from '../Versions';
import toast from 'features/toast';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { useForm } from 'react-final-form';

interface FormParams {
  models: IOption[];
  onSubmit: any;
  onActorSearch?: (actorName: string) => void;
}

interface AdditionalField {
  name: string;
  code: string;
  options: IOption[];
  optionLabelField?: string;
}

function FormActions({
  searchLabel,
  onResetExtra
}: {
  searchLabel: string;
  onResetExtra?: () => void;
}) {
  const form = useForm();
  const { t } = useTranslation();

  return (
    <div className="w-full mt-4 py-auto flex flex-wrap justify-end gap-2">
      <Button
        type="button"
        color="error"
        variant="outlined"
        className="w-full"
        onClick={() => {
          form.reset();
          onResetExtra?.();
        }}>
        {t('actions.reset')}
      </Button>
      <Button type="submit" variant="contained" className="w-full">
        {searchLabel}
      </Button>
    </div>
  );
}

function LogsFilterForm(params: FormParams) {
  const { models, onSubmit, onActorSearch } = params;
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [objectOptions, setObjectOptions] = useState<IOption[]>([]);
  const [optionLabelField, setOptionLabelField] = useState<string | undefined>(undefined);
  const [additionalFilter, setAdditionalFilter] = useState<AdditionalField[]>([]);
  const [mode, setMode] = useState<'model' | 'actor'>('model');
  const { t } = useTranslation();

  useEffect(() => {
    setObjectOptions([]);
    setAdditionalFilter([]);
    if (selectedModel) {
      const modelConfig = MODELS_MAPS?.[selectedModel.code];
      if (!modelConfig) {
        setOptionLabelField(undefined);
        return;
      }

      if (modelConfig.fields) {
        const fields = [];
        Promise.all(
          modelConfig.fields.map((item: any) => {
            const methodName = item.getMethod || 'getAllBaseInformation';
            return item.service[methodName]().then((res) => ({
              name: item.name,
              code: item.code,
              options: item.getMethod ? res.results : res,
              optionLabelField: item?.optionLabelField
            }));
          })
        ).then((updatedFields) => {
          setAdditionalFilter(updatedFields);
        });
        setAdditionalFilter(fields);
      } else {
        const service = modelConfig.service;
        if (!service) {
          setOptionLabelField(undefined);
          return;
        }

        if (modelConfig.getMethod) {
          service.getAllPaginated().then((res) => {
            if (res.results.length > 0) {
              setObjectOptions(res.results);
            } else {
              toast.info('Nessun oggetto disponibile per questo modello');
            }
            setObjectOptions(res.results);
          });
        } else {
          service.getAllBaseInformation().then((res) => {
            if (res.length > 0) {
              setObjectOptions(res);
            } else {
              toast.info('Nessun oggetto disponibile per questo modello');
            }
          });
        }
      }

      if (modelConfig.optionLabelField) {
        setOptionLabelField(modelConfig.optionLabelField);
      }
    }
  }, [selectedModel]);

  const handleSearchByActor = async (queryRaw: string) => {
    const query = String(queryRaw ?? '').trim();
    if (!query) return;
    onActorSearch?.(query);
  };

  const handleSubmit = async (values: any, form: any) => {
    if (mode === 'actor') {
      await handleSearchByActor(values?.actor_name);
      return;
    }

    onSubmit(values);
  };

  return (
    <div>
      <Stack spacing={2}>
        <ReactFinalForm
          onSubmit={(values: any, form: any) => handleSubmit(values, form)}
          formId="filterLogs"
          hideControls>
          <div className="w-full flex justify-start">
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={mode}
              onChange={(_, next) => {
                if (next) setMode(next);
              }}
              size="small">
              <ToggleButton value="model">Modello</ToggleButton>
              <ToggleButton value="actor">Autore</ToggleButton>
            </ToggleButtonGroup>
          </div>

          {mode === 'model' ? (
            <div className="flex flex-wrap mb-2 w-full">
              <div className="w-full flex gap-4">
                <div className="w-1/2">
                  <IdentityField
                    name="model"
                    component={AutocompleteAdapter}
                    options={models}
                    optionLabel="label"
                    optionValue="code"
                    label={t('global.model')}
                    required
                    setValue={setSelectedModel}
                    value={selectedModel}
                  />
                </div>
                {additionalFilter &&
                  additionalFilter.map((item: AdditionalField, index: number) => (
                    <div key={index} className="w-1/4">
                      <IdentityField
                        name={item.code}
                        label={item.name}
                        component={AutocompleteAdapter}
                        options={item.options}
                        optionLabel={item.optionLabelField ?? 'description'}
                        type="text"
                        required
                      />
                    </div>
                  ))}
                <div className="w-1/2">
                  {objectOptions.length > 0 ? (
                    <IdentityField
                      name="object"
                      label={t('global.object')}
                      component={AutocompleteAdapter}
                      options={objectOptions}
                      optionLabel={optionLabelField ?? 'description'}
                      type="text"
                      required={additionalFilter.length === 0}
                    />
                  ) : (
                    <IdentityField
                      name="object"
                      label={t('global.object')}
                      component={TextFieldAdapter}
                      type="text"
                      placeholder="ID o valore"
                      required={additionalFilter.length === 0}
                    />
                  )}
                </div>
              </div>

              <FormActions
                searchLabel={t('global.search')}
                onResetExtra={() => {
                  setSelectedModel(null);
                  setObjectOptions([]);
                  setAdditionalFilter([]);
                }}
              />
            </div>
          ) : (
            <>
              <Divider />
              <div className="flex flex-wrap mb-2 w-full">
                <div className="w-full flex gap-4">
                  <div className="w-1/2">
                    <IdentityField
                      name="actor_name"
                      label="Nome utente"
                      component={TextFieldAdapter}
                      type="text"
                    />
                  </div>
                </div>

                <FormActions
                  searchLabel={t('global.search')}
                  onResetExtra={() => {
                    onActorSearch?.('');
                  }}
                />
              </div>
            </>
          )}
        </ReactFinalForm>
      </Stack>
    </div>
  );
}

export default LogsFilterForm;
